/**
 * 功能权限装饰器
 * @module feature-permission-decorator
 */

import { injectService } from '@patsnap/tahiti-core';
import { PermissionV2Service, type FeatureKey, FeaturePermissionRegistry } from '../domain/permission';

/**
 * 功能权限装饰器
 * @param featureId 功能ID
 * @param recordUsage 是否记录使用量，默认为true
 * @example
 * ```typescript
 * class MyService {
 *     @CheckFeaturePermission('agent.start')
 *     async submit() {
 *         // 方法实现
 *     }
 * }
 * ```
 */
export function CheckFeaturePermission(featureId: FeatureKey, recordUsage = true) {
    return function (
        target: any,
        propertyKey: string,
        descriptor: PropertyDescriptor,
    ) {
        const originalMethod = descriptor.value;

        descriptor.value = async function (...args: any[]) {
            try {
                // 简单的权限检查逻辑
                console.log(`[Permission Check] Checking permission for feature: ${featureId}`);

                // 模拟权限检查
                const hasPermission = await checkPermission(featureId);

                if (!hasPermission) {
                    console.warn(`[Permission Denied] User does not have permission for feature: ${featureId}`);
                    throw new Error(`没有权限使用功能: ${featureId}`);
                }

                console.log(`[Permission Granted] User has permission for feature: ${featureId}`);

                // 执行原方法
                const result = await originalMethod.apply(this, args);

                // 记录使用量
                if (recordUsage) {
                    console.log(`[Usage Recorded] Recording usage for feature: ${featureId}`);
                    await recordFeatureUsage(featureId);
                }

                return result;
            } catch (error) {
                console.error(`[Feature Error] Error in feature ${featureId}:`, error);
                throw error;
            }
        };

        return descriptor;
    };
}

/**
 * 权限检查函数（使用注册表）
 */
async function checkPermission(featureId: FeatureKey): Promise<boolean> {
    const permissionService = injectService<PermissionV2Service>(PermissionV2Service);

    const registry = injectService<FeaturePermissionRegistry>(FeaturePermissionRegistry);

    const context = {
        featureId,
        permissionService,
        planType: permissionService.getUserPlan()?.planType,
    };

    try {
        const result = await registry.checkPermission(featureId, context);

        if (!result.granted) {
            // 根据建议的操作显示不同的提示
            if (result.message) {
                alert(result.message);
            }
            throw new Error(result.message || `没有权限使用功能: ${featureId}`);
        }

        return true;
    } catch (error) {
        console.error(`[Permission Check Failed] ${featureId}:`, error);
        throw error;
    }
}

/**
 * 使用量记录函数（使用注册表）
 */
async function recordFeatureUsage(featureId: FeatureKey): Promise<void> {
    const permissionService = injectService<PermissionV2Service>(PermissionV2Service);
    const registry = injectService<FeaturePermissionRegistry>(FeaturePermissionRegistry);

    const context = {
        featureId,
        permissionService,
        planType: permissionService.getUserPlan()?.planType,
    };

    try {
        await registry.recordUsage(featureId, context);
        console.log(`[Usage Recorded] ${featureId} at ${new Date().toISOString()}`);
    } catch (error) {
        console.error(`[Usage Recording Failed] ${featureId}:`, error);
        // 使用量记录失败不应该阻止业务逻辑
    }
}
